import React, { useEffect, useState, useMemo } from "react";
import dayjs from "dayjs";
import { Tag, Spin, Dropdown } from "antd";
import ResponsiveTableWrapper from "../../components/ResponsiveTableWrapper";
import ResponsiveModal from "../../components/ResponsiveModal";
import {
  useMemberRequests,
  useMemberRequestDetail,
  useApproveMemberRequest,
  useRejectMemberRequest,
} from "../../hooks/api";
import { notifyUser } from "../../util/util";

const MemberRequestsTable = () => {
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [selectedId, setSelectedId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionRecord, setActionRecord] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [remark, setRemark] = useState("");

  const {
    data: requestData,
    isLoading,
    error,
    refetch,
  } = useMemberRequests(pagination.current - 1, pagination.pageSize);

  const tableData = useMemo(() => {
    const rows = requestData?.content || [];
    if (!searchText) return rows;
    const lower = searchText.toLowerCase();
    return rows.filter((item) =>
      Object.values(item).some((val) =>
        String(val).toLowerCase().includes(lower)
      )
    );
  }, [requestData, searchText]);

  useEffect(() => {
    if (requestData) {
      setPagination((prev) => ({
        ...prev,
        total: requestData.totalElements || 0,
      }));
    }
  }, [requestData]);

  const handleSearch = (e) => {
    setSearchText(e.target.value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleView = (record) => {
    setSelectedId(record.id);
    setIsModalOpen(true);
  };

  const handleApprove = (record) => {
    setActionRecord(record);
    setActionType("approve");
    setRemark("");
    setActionModalOpen(true);
  };

  const handleReject = (record) => {
    setActionRecord(record);
    setActionType("reject");
    setRemark("");
    setActionModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedId(null);
  };

  const closeActionModal = () => {
    setActionModalOpen(false);
    setActionRecord(null);
    setActionType(null);
    setRemark("");
  };

  const approveMutation = useApproveMemberRequest();
  const rejectMutation = useRejectMemberRequest();

  const handleActionConfirm = () => {
    if (!actionRecord || !remark.trim()) {
      notifyUser("error", "Please enter a remark", "Error");
      return;
    }
    const payload = { requestId: actionRecord.id, remark: remark.trim() };
    if (actionType === "approve") {
      approveMutation.mutate(payload, { onSuccess: closeActionModal });
    } else {
      rejectMutation.mutate(payload, { onSuccess: closeActionModal });
    }
  };

  const { data: detail, isLoading: detailLoading } =
    useMemberRequestDetail(selectedId);

  const statusTag = (status) => {
    const colorMap = {
      PENDING: "warning",
      APPROVED: "success",
      REJECTED: "error",
    };
    return <Tag color={colorMap[status] || "default"}>{status}</Tag>;
  };

  const columns = [
    {
      title: "Member Number",
      dataIndex: "memberNumber",
      key: "memberNumber",
      width: 180,
      render: (text) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {text || "N/A"}
        </div>
      ),
    },
    {
      title: "Full Name",
      key: "fullName",
      width: 150,
      render: (_, record) => {
        const r = record.request || {};
        const name = `${r.firstName || ""} ${r.surname || ""}`.trim();
        return (
          <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            {name || "N/A"}
          </div>
        );
      },
    },
    {
      title: "Employee Email",
      key: "email",
      width: 200,
      render: (_, record) => {
        const email = record.request?.email;
        return (
          <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            {email || "N/A"}
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "requestStatus",
      key: "requestStatus",
      width: 120,
      render: (text) => statusTag(text),
    },
    {
      title: "Request Date",
      dataIndex: "requestDate",
      key: "requestDate",
      width: 160,
      render: (text) => {
        const formatted = text && dayjs(text).isValid()
          ? dayjs(text).format("MMM D, YYYY - hh:mm A")
          : text;
        return (
          <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            {formatted}
          </div>
        );
      },
    },
    {
      title: "Reviewed By",
      dataIndex: "reviewedBy",
      key: "reviewedBy",
      width: 180,
      render: (text) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {text || "N/A"}
        </div>
      ),
    },
    {
      title: "Reviewed At",
      dataIndex: "reviewedAt",
      key: "reviewedAt",
      width: 160,
      render: (text) => {
        const formatted = text && dayjs(text).isValid()
          ? dayjs(text).format("MMM D, YYYY - hh:mm A")
          : text || "N/A";
        return (
          <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            {formatted}
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 80,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "view",
                label: "View",
                onClick: () => handleView(record),
              },
              ...(record.requestStatus === "PENDING"
                ? [
                    {
                      key: "approve",
                      label: "Approve",
                      onClick: () => handleApprove(record),
                    },
                    {
                      key: "reject",
                      label: "Reject",
                      onClick: () => handleReject(record),
                    },
                  ]
                : []),
            ],
          }}
          overlayStyle={{ boxShadow: "none" }}
        >
          <span style={{ cursor: "pointer", fontSize: 20 }} title="Actions">
            <i className="bi bi-three-dots-vertical" />
          </span>
        </Dropdown>
      ),
    },
  ];

  if (error) {
    return (
      <div className="p-4">
        <div className="alert alert-danger">
          Error loading requests: {error.message}
          <button className="btn btn-primary ms-2" onClick={refetch} title="Retry">
            <i className="bi bi-arrow-clockwise me-1" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div className="d-flex align-items-center gap-2">
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: "rgba(0, 150, 119, 0.1)",
              color: "#009677",
              fontSize: 18,
            }}
          >
            <i className="bi bi-list-check" />
          </div>
          <h5 className="fw-bold mb-0" style={{ color: "#1f2937" }}>
            Member Requests
          </h5>
        </div>
        <div className="position-relative">
          <i
            className="bi bi-search position-absolute"
            style={{
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#94a3b8",
              fontSize: 14,
            }}
          />
          <input
            type="text"
            className="form-control"
            placeholder="Search requests..."
            value={searchText}
            onChange={handleSearch}
            style={{
              paddingLeft: 36,
              borderRadius: 10,
              borderColor: "#e2e8f0",
              minWidth: 260,
              fontSize: 14,
              boxShadow: "none",
            }}
          />
        </div>
      </div>
      <ResponsiveTableWrapper
        columns={columns}
        dataSource={tableData}
        loading={isLoading}
        rowKey="id"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} requests`,
        }}
        onChange={handleTableChange}
      />

      <ResponsiveModal
        open={isModalOpen}
        onCancel={closeModal}
        title="Member Request Details"
        footer={null}
      >
        {detailLoading ? (
          <div className="text-center py-5">
            <Spin />
          </div>
        ) : detail ? (
          <div className="py-2">
            <div className="mb-3">
              <span className="fw-bold me-2">Status:</span>
              {statusTag(detail.requestStatus)}
            </div>
            <div className="mb-3">
              <span className="fw-bold me-2">Request Date:</span>
              {detail.requestDate && dayjs(detail.requestDate).isValid()
                ? dayjs(detail.requestDate).format("MMM D, YYYY - hh:mm A")
                : detail.requestDate}
            </div>
            {detail.reviewedBy && (
              <div className="mb-3">
                <span className="fw-bold me-2">Reviewed By:</span>
                {detail.reviewedBy}
              </div>
            )}
            {detail.reviewedAt && (
              <div className="mb-3">
                <span className="fw-bold me-2">Reviewed At:</span>
                {dayjs(detail.reviewedAt).isValid()
                  ? dayjs(detail.reviewedAt).format("MMM D, YYYY - hh:mm A")
                  : detail.reviewedAt}
              </div>
            )}
            <hr />
            {detail.request && (
              <>
                <h6 className="fw-bold mb-3">Member Information</h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <strong>First Name:</strong> {detail.request.firstName || "N/A"}
                  </div>
                  <div className="col-md-6">
                    <strong>Surname:</strong> {detail.request.surname || "N/A"}
                  </div>
                  <div className="col-md-6">
                    <strong>Other Names:</strong> {detail.request.otherNames || "N/A"}
                  </div>
                  <div className="col-md-6">
                    <strong>Ghana Card:</strong> {detail.request.ghanaCardNumber || "N/A"}
                  </div>
                  <div className="col-md-6">
                    <strong>Date of Birth:</strong> {detail.request.dateOfBirth || "N/A"}
                  </div>
                  <div className="col-md-6">
                    <strong>Gender:</strong> {detail.request.gender || "N/A"}
                  </div>
                  <div className="col-md-6">
                    <strong>Nationality:</strong> {detail.request.nationality || "N/A"}
                  </div>
                  <div className="col-md-6">
                    <strong>Email:</strong> {detail.request.email || "N/A"}
                  </div>
                  <div className="col-md-6">
                    <strong>Phone:</strong> {detail.request.phoneNumber || "N/A"}
                  </div>
                  <div className="col-md-6">
                    <strong>SSNIT:</strong> {detail.request.ssnitNumber || "N/A"}
                  </div>
                  <div className="col-md-6">
                    <strong>Salary:</strong> {detail.request.salary ?? "N/A"}
                  </div>
                </div>

                {detail.request.beneficiaries &&
                  detail.request.beneficiaries.length > 0 && (
                    <>
                      <hr />
                      <h6 className="fw-bold mb-3">Beneficiaries</h6>
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>ID Type</th>
                              <th>ID Number</th>
                              <th>Mobile</th>
                              <th>Relationship</th>
                              <th>DOB</th>
                              <th>%</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detail.request.beneficiaries.map((b, i) => (
                              <tr key={i}>
                                <td>
                                  {b.firstName} {b.otherNames}
                                </td>
                                <td>{b.identificationType ? b.identificationType.replace(/_/g, " ") : ""}</td>
                                <td>{b.idNumber}</td>
                                <td>{b.mobileNumber}</td>
                                <td>{b.relationship}</td>
                                <td>{b.dateOfBirth}</td>
                                <td>{b.percentageAllocation}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
              </>
            )}
            {detail.requestStatus === "PENDING" && (
              <div className="d-flex gap-2 justify-content-end mt-4 mobile-stack-buttons" style={{ whiteSpace: "nowrap" }}>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    closeModal();
                    handleApprove(detail);
                  }}
                  title="Approve"
                >
                  Approve
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    closeModal();
                    handleReject(detail);
                  }}
                  title="Reject"
                >
                  Reject
                </button>
              </div>
            )}
          </div>
        ) : null}
      </ResponsiveModal>

      <ResponsiveModal
        open={actionModalOpen}
        onCancel={closeActionModal}
        title={actionType === "approve" ? "Approve Request" : "Reject Request"}
        footer={null}
      >
        <div className="py-2">
          <p className="mb-2">
            {actionType === "approve"
              ? "Approve this member request?"
              : "Reject this member request?"}
          </p>
          <div className="mb-3">
            <omk-text-field
              label="Comment"
              value={remark}
              type="textarea"
              rows={4}
              onInput={(e) => {
                const val = e.target.value;
                setRemark(val.length > 250 ? val.slice(0, 250) : val);
              }}
            />
            <div className="text-end text-muted mt-1" style={{ fontSize: 12 }}>
              {remark.length}/250
            </div>
          </div>
          <div className="d-flex justify-content-end gap-2 mobile-stack-buttons">
            <button className="btn btn-white" onClick={closeActionModal} title="Cancel">
              Cancel
            </button>
            <button
              className={`btn ${
                actionType === "approve" ? "btn-primary" : "btn-danger"
              }`}
              title={actionType === "approve" ? "Approve" : "Reject"}
              onClick={handleActionConfirm}
              disabled={
                approveMutation.isPending || rejectMutation.isPending || !remark.trim()
              }
            >
              {approveMutation.isPending || rejectMutation.isPending
                ? "Processing..."
                : actionType === "approve"
                ? "Approve"
                : "Reject"}
            </button>
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
};

export default MemberRequestsTable;
